import { AxiosError } from "axios";
import { useCallback, useContext, useEffect, useState } from "react";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import {
  JiraAvailableProject,
  BoardingStatus,
  CanLogin,
  Tool,
  User,
  Task,
  ToolConfig,
  BlackDuckProjectGroupMembership,
  BlackDuckProjectGroup,
  Contract,
  ScheduleKind,
  Role,
  TaskListKind,
  DisplayedCollection,
  Collection,
  DatasetListItem,
  DatasetMembership,
  TaskKindDiscriminants,
  LogSearchParams,
  CatalogItem,
  PolarisObject,
  CreateSupportTicketMeta,
  Resource,
  LegalUnit,
  TableauSiteRole,
  CarolineMetrics,
  TableauProject,
  TableauProjectRole,
} from "./backendTypes";
import { HttpClientContext, X_REQUEST_ID } from "./client";
import { type RootState, type AppDispatch, setUserMapState } from "./store";

const DURATION = 1000;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export function useMetricsCaroline() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [metrics, setMetrics] = useState<CarolineMetrics | null>(null);

  const client = useContext(HttpClientContext);

  const loadMetrics = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.get<CarolineMetrics>("/api/metrics/caroline")
      .then((res) => setMetrics(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  return { metrics, loading, error, reload: loadMetrics };
}

export function useTableauProjectOwner(projectId: string | undefined) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [owner, setOwner] = useState<User | null>(null);

  const client = useContext(HttpClientContext);

  const loadOwner = useCallback(() => {
    if (projectId === undefined) return;

    setLoading(true);
    setError(undefined);

    client
      ?.get<User>(`/api/tools/${Tool.Tableau}/project/${projectId}/owner`)
      .then((res) => setOwner(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, projectId]);

  useEffect(() => {
    loadOwner();
  }, [loadOwner]);

  return { owner, loading, error, reload: loadOwner };
}

export function usePendingTableauSiteRoleChangeTask(userId: number) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [task, setTask] = useState<Task | null>(null);

  const client = useContext(HttpClientContext);

  const loadPendingTask = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.get<Task>(
        `/api/tools/${Tool.Tableau}/pending_site_role_change_task/${userId}`
      )
      .then((res) => setTask(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, userId]);

  useEffect(() => {
    loadPendingTask();
  }, [loadPendingTask]);

  useEffect(() => {
    let intervalId: string | number | NodeJS.Timeout | undefined;

    if (task !== null) {
      if (task.status.kind !== "Done" && task.status.kind !== "Cancelled") {
        intervalId = setInterval(() => {
          client?.get<Task>(`/api/tasks/${task.id}`).then((res) => {
            if (JSON.stringify(res.data) !== JSON.stringify(task))
              setTask(res.data);
          });
        }, DURATION);
      }
    }

    return () => clearInterval(intervalId);
  }, [client, task]);

  return { task, loading, error, reload: loadPendingTask };
}

export function usePendingTableauProjectRoleChangeTask(
  userId: number,
  projectId: string
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [task, setTask] = useState<Task | null>(null);

  const client = useContext(HttpClientContext);

  const loadPendingTask = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.get<Task>(
        `/api/tools/${Tool.Tableau}/pending_project_role_change_task/${userId}/${projectId}`
      )
      .then((res) => setTask(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, projectId, userId]);

  useEffect(() => {
    loadPendingTask();
  }, [loadPendingTask]);

  useEffect(() => {
    let intervalId: string | number | NodeJS.Timeout | undefined;

    if (task !== null) {
      if (task.status.kind !== "Done" && task.status.kind !== "Cancelled") {
        intervalId = setInterval(() => {
          client?.get<Task>(`/api/tasks/${task.id}`).then((res) => {
            if (JSON.stringify(res.data) !== JSON.stringify(task))
              setTask(res.data);
          });
        }, DURATION);
      }
    }

    return () => clearInterval(intervalId);
  }, [client, task]);

  return { task, loading, error, reload: loadPendingTask };
}

export function useTableauProjectRole(projectId: string, target: number) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [role, setRole] = useState<TableauProjectRole | null>(null);

  const client = useContext(HttpClientContext);

  const loadRole = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.get<TableauProjectRole>(
        `/api/tools/${Tool.Tableau}/project_role/${projectId}/${target}`
      )
      .then((res) => setRole(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, projectId, target]);

  useEffect(() => {
    loadRole();
  }, [loadRole]);

  return { role, loading, error, reload: loadRole };
}

export function useTableauUserSiteRole(userId: number | null) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [role, setRole] = useState<TableauSiteRole | null>(null);

  const client = useContext(HttpClientContext);

  const loadRole = useCallback(() => {
    if (userId === null) return;

    setLoading(true);
    setError(undefined);

    client
      ?.get<TableauSiteRole>(
        `/api/tools/${Tool.Tableau}/user/${userId}/site_role`
      )
      .then((res) => setRole(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [userId, client]);

  useEffect(() => {
    loadRole();
  }, [loadRole]);

  return { role, loading, error, reload: loadRole };
}

export function useLegalUnits() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [units, setUnits] = useState<LegalUnit[]>([]);

  const client = useContext(HttpClientContext);

  const loadLegalUnits = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.get<LegalUnit[]>("/api/catalog/legal_units")
      .then((res) => {
        res.data.sort((l, r) => l.name.localeCompare(r.name));
        setUnits(res.data);
      })
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client]);

  useEffect(() => {
    loadLegalUnits();
  }, [loadLegalUnits]);

  return { units, loading, error, reload: loadLegalUnits };
}

export function usePendingSupportTask(resource: Resource, userId: number) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [task, setTask] = useState<Task | null>(null);

  const client = useContext(HttpClientContext);

  const loadPendingTask = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.get<Task>(
        `/api/tasks/pending_support_task?resourceKind=${resource.kind}&resourceId=${resource.inner.id}&userId=${userId}`
      )
      .then((res) => setTask(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, resource.inner.id, resource.kind, userId]);

  useEffect(() => {
    loadPendingTask();
  }, [loadPendingTask]);

  useEffect(() => {
    let intervalId: string | number | NodeJS.Timeout | undefined;

    if (task !== null) {
      if (task.status.kind !== "Done" && task.status.kind !== "Cancelled") {
        intervalId = setInterval(() => {
          client?.get<Task>(`/api/tasks/${task.id}`).then((res) => {
            if (JSON.stringify(res.data) !== JSON.stringify(task))
              setTask(res.data);
          });
        }, DURATION);
      }
    }

    return () => clearInterval(intervalId);
  }, [client, task]);

  return { task, loading, error, reload: loadPendingTask };
}

export function useCreateSupportTicketMeta(serviceName: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [meta, setMeta] = useState<CreateSupportTicketMeta | null>(null);

  const client = useContext(HttpClientContext);

  const loadMeta = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.get<CreateSupportTicketMeta>(
        `/api/tools/${Tool.Jira}/support/meta_for_ticket_creation?serviceName=${serviceName}`
      )
      .then((res) => setMeta(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, serviceName]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  return { meta, loading, error, reload: loadMeta };
}

export function usePolarisGroupMembership(
  userId: number,
  groupId: string | undefined
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [membership, setMembership] = useState<boolean | null>(null);

  const client = useContext(HttpClientContext);

  const loadMembership = useCallback(() => {
    if (groupId === undefined || groupId === null) return;

    setLoading(true);
    setError(undefined);

    client
      ?.get<boolean>(
        `/api/tools/${Tool.Polaris}/groups/${groupId}/user/${userId}`
      )
      .then((res) => setMembership(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, userId, groupId]);

  useEffect(() => {
    loadMembership();
  }, [loadMembership]);

  return { membership, loading, error, reload: loadMembership };
}

export function usePendingPolarisGroupAccessTask(
  userId: number,
  groupId: string | undefined
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [task, setTask] = useState<Task | null>(null);

  const client = useContext(HttpClientContext);

  const loadPendingTask = useCallback(() => {
    if (groupId === undefined || groupId === null) return;

    setLoading(true);
    setError(undefined);

    client
      ?.get<Task>(
        `/api/tools/${Tool.Polaris}/pending_group_access_task/${userId}/${groupId}`
      )
      .then((res) => setTask(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, userId, groupId]);

  useEffect(() => {
    loadPendingTask();
  }, [loadPendingTask]);

  useEffect(() => {
    let intervalId: string | number | NodeJS.Timeout | undefined;

    if (task !== null) {
      if (task.status.kind !== "Done" && task.status.kind !== "Cancelled") {
        intervalId = setInterval(() => {
          client?.get<Task>(`/api/tasks/${task.id}`).then((res) => {
            if (JSON.stringify(res.data) !== JSON.stringify(task))
              setTask(res.data);
          });
        }, DURATION);
      }
    }

    return () => clearInterval(intervalId);
  }, [client, task]);

  return { task, loading, error, reload: loadPendingTask };
}

export function usePolarisGroups(skip: boolean) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [groups, setGroups] = useState<PolarisObject[]>([]);

  const client = useContext(HttpClientContext);

  const loadGroups = useCallback(() => {
    if (skip) return;
    setLoading(true);

    client
      ?.get<PolarisObject[]>(`/api/tools/${Tool.Polaris}/groups`)
      .then((res) => setGroups(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, skip]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  return { groups, loading, error, reload: loadGroups };
}

export function useUser(id: number | null) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [user, setUser] = useState<User | null>(null);

  const cachedUser = useAppSelector((state) => state.users[id || 0]);
  const dispatch = useAppDispatch();

  const client = useContext(HttpClientContext);

  const loadUser = useCallback(
    (controller?: AbortController) => {
      return client?.get<User>(`/api/users/${id}`, {
        signal: controller?.signal,
      });
    },
    [client, id]
  );

  useEffect(() => {
    if (id === null) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    navigator.locks.request("loadUser", async () => {
      if (cachedUser !== undefined) {
        const now = new Date();

        if (now.getTime() - cachedUser.ts < 60000) {
          setUser(cachedUser.value);
          setLoading(false);
          return;
        }
      }

      if (controller.signal.aborted) return;

      try {
        const user = await loadUser(controller);
        if (!controller.signal.aborted && user !== undefined) {
          dispatch(setUserMapState(user.data));
          setUser(user.data);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          const e = error as AxiosError;
          setError(e.response?.headers[X_REQUEST_ID]);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    });

    return () => controller.abort();
  }, [cachedUser, client, dispatch, id, loadUser, loading, user]);

  return { user, error, loading };
}

export function useCollection(id: string, skip: boolean) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [collection, setCollection] = useState<Collection | null>(null);

  const client = useContext(HttpClientContext);

  const loadCollection = useCallback(() => {
    if (skip) return;
    setLoading(true);
    setError(undefined);

    client
      ?.get<Collection>(`/api/datasets/${id}`)
      .then((res) => setCollection(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, id, skip]);

  useEffect(() => {
    loadCollection();
  }, [loadCollection]);

  return { collection, error, loading, reload: loadCollection };
}

export function useDatasetMembership(
  collectionId: string,
  datasetId: string,
  userId: number
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [membership, setMembership] = useState<DatasetMembership | null>(null);

  const client = useContext(HttpClientContext);

  const loadMembership = useCallback(
    (controller?: AbortController) => {
      setLoading(true);
      setError(undefined);

      client
        ?.get<DatasetMembership>(
          `/api/datasets/${collectionId}/${datasetId}/${userId}`,
          { signal: controller?.signal }
        )
        .then((res) => {
          if (!controller?.signal.aborted) setMembership(res.data);
        })
        .catch((e: AxiosError) => {
          if (!controller?.signal.aborted)
            setError(e.response?.headers[X_REQUEST_ID]);
        })
        .finally(() => {
          if (!controller?.signal.aborted) setLoading(false);
        });
    },
    [client, collectionId, datasetId, userId]
  );

  useEffect(() => {
    const controller = new AbortController();

    loadMembership(controller);

    return () => controller.abort();
  }, [loadMembership]);

  return { membership, error, loading, reload: loadMembership };
}

export function useDatasets(collection_id: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [datasets, setCollections] = useState<DatasetListItem[]>([]);

  const client = useContext(HttpClientContext);

  const loadDatasets = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.get<DatasetListItem[]>(`/api/datasets/${collection_id}/`)
      .then((res) => setCollections(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, collection_id]);

  useEffect(() => {
    loadDatasets();
  }, [loadDatasets]);

  return { datasets, error, loading, reload: loadDatasets };
}

export function useDisplayedCollections() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [collections, setCollections] = useState<DisplayedCollection[]>([]);

  const client = useContext(HttpClientContext);

  const loadDisplayedCollections = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.get<DisplayedCollection[]>("/api/datasets")
      .then((res) => setCollections(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client]);

  useEffect(() => {
    loadDisplayedCollections();
  }, [loadDisplayedCollections]);

  return { collections, error, loading, reload: loadDisplayedCollections };
}

export function useCatalogItems() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);

  const client = useContext(HttpClientContext);

  const loadCatalogItems = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.get<CatalogItem[]>("/api/catalog")
      .then((res) => setCatalogItems(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client]);

  useEffect(() => {
    loadCatalogItems();
  }, [loadCatalogItems]);

  return { catalogItems, error, loading, reload: loadCatalogItems };
}

export function useLogs(params: LogSearchParams | null) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [logs, setLogs] = useState<string[]>([]);

  const client = useContext(HttpClientContext);

  const loadLogs = useCallback(() => {
    if (params === null) return;

    setLoading(true);
    setError(undefined);

    client
      ?.post<string[]>("/api/logs", params)
      .then((res) => setLogs(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, params]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  return { logs, error, loading, reload: loadLogs };
}

export function useTasks(
  listKind: TaskListKind,
  taskKind: TaskKindDiscriminants | null,
  taskSubkind: string | null
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [tasks, setTasks] = useState<Task[]>([]);

  const client = useContext(HttpClientContext);

  const loadTasks = useCallback(
    (controller?: AbortController) => {
      setLoading(true);
      setError(undefined);

      client
        ?.get<Task[]>(
          `/api/tasks?list_kind=${listKind}${
            taskKind !== null ? `&task_kind=${taskKind}` : ""
          }${taskSubkind !== null ? `&task_subkind=${taskSubkind}` : ""}`,
          {
            signal: controller?.signal,
          }
        )
        .then((res) => {
          if (!controller?.signal.aborted)
            setTasks(
              res.data.sort((l, r) => -l.created.localeCompare(r.created))
            );
        })
        .catch((e: AxiosError) => {
          if (!controller?.signal.aborted)
            setError(e.response?.headers[X_REQUEST_ID]);
        })
        .finally(() => {
          if (!controller?.signal.aborted) setLoading(false);
        });
    },
    [client, listKind, taskKind, taskSubkind]
  );

  useEffect(() => {
    const controller = new AbortController();

    loadTasks(controller);

    return () => controller.abort();
  }, [loadTasks]);

  // useEffect(() => {
  //   if (listKind === TaskListKind.Archived) return;

  //   const intervalId = setInterval(() => {
  //     client
  //       ?.get<Task[]>(
  //         `/api/tasks?list_kind=${listKind}${
  //           taskKind !== null ? `&task_kind=${taskKind}` : ""
  //         }${taskSubkind !== null ? `&task_subkind=${taskSubkind}` : ""}`
  //       )
  //       .then((res) => {
  //         if (JSON.stringify(res.data) !== JSON.stringify(tasks))
  //           setTasks(
  //             res.data.sort((l, r) => -l.created.localeCompare(r.created))
  //           );
  //       });
  //   }, DURATION);

  //   return () => clearInterval(intervalId);
  // }, [client, listKind, taskKind, tasks, taskSubkind]);

  const clear = useCallback(() => {
    setTasks([]);
  }, []);

  return { tasks, error, loading, reload: loadTasks, clear };
}

export function useTask(taskId: string | undefined) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [task, setTask] = useState<Task | null>(null);

  const client = useContext(HttpClientContext);

  const loadTask = useCallback(
    (controller?: AbortController) => {
      if (taskId === undefined || taskId === null) return;
      setLoading(true);

      client
        ?.get<Task>(`/api/tasks/${taskId}`, { signal: controller?.signal })
        .then((res) => {
          if (!controller?.signal.aborted) setTask(res.data);
        })
        .catch((e: AxiosError) => {
          if (!controller?.signal.aborted) {
            if (e.response?.status === 404) setTask(null);
            else setError(e.response?.headers[X_REQUEST_ID]);
          }
        })
        .finally(() => {
          if (!controller?.signal.aborted) setLoading(false);
        });
    },
    [client, taskId]
  );

  useEffect(() => {
    const controller = new AbortController();

    loadTask(controller);

    return () => controller.abort();
  }, [loadTask]);

  useEffect(() => {
    let intervalId: string | number | NodeJS.Timeout | undefined;

    if (task !== null) {
      if (task.status.kind !== "Done" && task.status.kind !== "Cancelled") {
        intervalId = setInterval(() => {
          client
            ?.get<Task>(`/api/tasks/${task.id}`)
            .then((res) => {
              if (JSON.stringify(res.data) !== JSON.stringify(task))
                setTask(res.data);
            })
            .catch((e: AxiosError) => {
              if (e.response?.status === 404) setTask(null);
              else setError(e.response?.headers[X_REQUEST_ID]);
            });
        }, DURATION);
      }
    }

    return () => clearInterval(intervalId);
  }, [client, loadTask, task]);

  return { task, loading, error, reload: loadTask };
}

export function useScheduledTask(schedule: ScheduleKind | undefined) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [task, setTask] = useState<Task | null>(null);

  const client = useContext(HttpClientContext);

  const loadTask = useCallback(() => {
    if (schedule === undefined) return;

    setLoading(true);

    client
      ?.get<Task>(`/api/schedule/${schedule}/task`)
      .then((res) => setTask(res.data))
      .catch((e: AxiosError) => {
        if (e.response?.status === 404) setTask(null);
        else setError(e.response?.headers[X_REQUEST_ID]);
      })
      .finally(() => setLoading(false));
  }, [client, schedule]);

  useEffect(() => {
    loadTask();
  }, [loadTask]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      client
        ?.get<Task>(`/api/schedule/${schedule}/task`)
        .then((res) => {
          if (JSON.stringify(res.data) !== JSON.stringify(task))
            setTask(res.data);
        })
        .catch((e: AxiosError) => {
          if (e.response?.status === 404) setTask(null);
          else setError(e.response?.headers[X_REQUEST_ID]);
        });
    }, DURATION);

    return () => clearInterval(intervalId);
  }, [client, loadTask, schedule, task]);

  return { task, loading, error, reload: loadTask };
}

export function useApprover(
  taskId: string,
  approvalIndex: number | undefined,
  skip: boolean
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [approver, setApprover] = useState<User | null>(null);

  const client = useContext(HttpClientContext);

  const loadApprover = useCallback(() => {
    if (approvalIndex === undefined) {
      setError("index is undefined");
      return;
    }

    setLoading(true);
    setError(undefined);

    client
      ?.get<User>(`/api/tasks/${taskId}/approval/${approvalIndex}/approver`)
      .then((res) => setApprover(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, taskId, approvalIndex]);

  useEffect(() => {
    if (skip) return;
    loadApprover();
  }, [loadApprover, skip]);

  return { approver, error, loading, reload: loadApprover };
}

export function useTools(as_items = true) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [tools, setTools] = useState<ToolConfig[]>([]);

  const client = useContext(HttpClientContext);

  const loadToolsList = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.get<ToolConfig[]>(`/api/tools?as_items=${as_items}`)
      .then((res) => setTools(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [as_items, client]);

  useEffect(loadToolsList, [loadToolsList]);

  return { tools, error, loading, reload: loadToolsList };
}

export function useContracts() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [contracts, setContracts] = useState<Contract[]>([]);

  const client = useContext(HttpClientContext);

  const loadContracts = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.get<Contract[]>("/api/contracts")
      .then((res) =>
        setContracts(res.data.sort((l, r) => l.name.localeCompare(r.name)))
      )
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client]);

  useEffect(loadContracts, [loadContracts]);

  return { contracts, error, loading, reload: loadContracts };
}

export function useContract(id: string | undefined) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [contract, setContract] = useState<Contract | null>(null);

  const client = useContext(HttpClientContext);

  const loadContract = useCallback(
    (controller?: AbortController) => {
      if (id === undefined || id === null || id === "") return;

      setLoading(true);
      setError(undefined);

      client
        ?.get<Contract>(`/api/contracts/${id}`, { signal: controller?.signal })
        .then((res) => setContract(res.data))
        .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
        .finally(() => setLoading(false));
    },
    [client, id]
  );

  useEffect(() => {
    const controller = new AbortController();

    loadContract(controller);

    return () => controller.abort();
  }, [loadContract]);

  return {
    contract: id !== undefined && id !== null && id !== "" ? contract : null,
    // contract,
    error,
    loading,
    reload: loadContract,
  };
}

export function useRoleMembers(role: Role) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [members, setMembers] = useState<User[]>([]);

  const client = useContext(HttpClientContext);

  const loadMembers = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.get<User[]>(`/api/roles/${role}/users`)
      .then((res) => setMembers(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, role]);

  useEffect(loadMembers, [loadMembers]);

  return { members, error, loading, reload: loadMembers };
}

export function useCanEmployeeLogin(userId: number, skip: boolean) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [canLogin, setCanLogin] = useState<CanLogin | null>(null);

  const client = useContext(HttpClientContext);

  useEffect(() => {
    if (skip) return;
    setLoading(true);

    client
      ?.get<CanLogin>(`/api/sessions/can_employee_login?id=${userId}`)
      .then((res) => setCanLogin(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, skip, userId]);

  return { canLogin, loading, error };
}

export function useToolBoardingStatus(tool: Tool, userId: number | null) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<BoardingStatus | null>(null);

  const client = useContext(HttpClientContext);

  const loadBoardingStatus = useCallback(() => {
    if (userId === null) return;

    setLoading(true);
    setError(undefined);

    client
      ?.get<BoardingStatus>(`/api/tools/${tool}/boarding/${userId}`)
      .then((res) => setStatus(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, tool, userId]);

  useEffect(() => {
    loadBoardingStatus();
  }, [loadBoardingStatus]);

  return { status, loading, error, reload: loadBoardingStatus };
}

export function useCatalogBoardingStatus(resource: Resource, userId: number) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<BoardingStatus | null>(null);

  const client = useContext(HttpClientContext);

  const loadBoardingStatus = useCallback(() => {
    if (resource.kind !== "Catalog") return;

    setLoading(true);
    setError(undefined);

    client
      ?.get<BoardingStatus>(
        `/api/catalog/${resource.inner.id}/boarding/${userId}`
      )
      .then((res) => setStatus(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, resource.inner.id, resource.kind, userId]);

  useEffect(() => {
    loadBoardingStatus();
  }, [loadBoardingStatus]);

  return {
    status,
    loading,
    error,
    reload: loadBoardingStatus,
  };
}

export function useJiraAvailableProjects(userId: number, skip: boolean) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [projects, setProjects] = useState<JiraAvailableProject[]>([]);

  const client = useContext(HttpClientContext);

  const loadProjects = useCallback(() => {
    if (skip) return;
    setLoading(true);

    client
      ?.get<JiraAvailableProject[]>(
        `/api/tools/${Tool.Jira}/available_projects/${userId}`
      )
      .then((res) => setProjects(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, skip, userId]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  return { projects, loading, error, reload: loadProjects };
}

export function useTableauProjects() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [projects, setProjects] = useState<TableauProject[]>([]);

  const client = useContext(HttpClientContext);

  const loadProjects = useCallback(() => {
    setLoading(true);

    client
      ?.get<TableauProject[]>(`/api/tools/${Tool.Tableau}/available_projects`)
      .then((res) => setProjects(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  return { projects, loading, error, reload: loadProjects };
}

export function usePendingJiraProjectAccessTask(
  userId: number,
  projectId: string
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [task, setTask] = useState<Task | null>(null);

  const client = useContext(HttpClientContext);

  const loadPendingTask = useCallback(() => {
    setLoading(true);

    client
      ?.get<Task>(
        `/api/tools/${Tool.Jira}/pending_project_access_task/${userId}/${projectId}`
      )
      .then((res) => setTask(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, userId, projectId]);

  useEffect(() => {
    loadPendingTask();
  }, [loadPendingTask]);

  useEffect(() => {
    let intervalId: string | number | NodeJS.Timeout | undefined;

    if (task !== null) {
      if (task.status.kind !== "Done" && task.status.kind !== "Cancelled") {
        intervalId = setInterval(() => {
          client?.get<Task>(`/api/tasks/${task.id}`).then((res) => {
            if (JSON.stringify(res.data) !== JSON.stringify(task))
              setTask(res.data);
          });
        }, DURATION);
      }
    }

    return () => clearInterval(intervalId);
  }, [client, task]);

  return { task, loading, error, reload: loadPendingTask };
}

export function useBlackDuckProjectGroups(skip: boolean) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [projectGroups, setProjectGroups] = useState<BlackDuckProjectGroup[]>(
    []
  );

  const client = useContext(HttpClientContext);

  const loadProjects = useCallback(() => {
    if (skip) return;
    setLoading(true);

    client
      ?.get<BlackDuckProjectGroup[]>(
        `/api/tools/${Tool.BlackDuck}/project_groups`
      )
      .then((res) => setProjectGroups(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, skip]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  return { projectGroups, loading, error, reload: loadProjects };
}

export function useBlackDuckProjectGroupMembership(
  userId: number,
  projectGroupId: string
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [membership, setMembership] =
    useState<BlackDuckProjectGroupMembership | null>(null);

  const client = useContext(HttpClientContext);

  const loadMembership = useCallback(() => {
    setLoading(true);

    client
      ?.get<BlackDuckProjectGroupMembership>(
        `/api/tools/${Tool.BlackDuck}/project_groups/${projectGroupId}/user/${userId}`
      )
      .then((res) => setMembership(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, projectGroupId, userId]);

  useEffect(() => {
    loadMembership();
  }, [loadMembership]);

  useEffect(() => {
    const task = membership?.task;

    let intervalId: string | number | NodeJS.Timeout | undefined;

    if (task !== undefined && task !== null) {
      if (task.status.kind !== "Done" && task.status.kind !== "Cancelled") {
        intervalId = setInterval(() => {
          client
            ?.get<BlackDuckProjectGroupMembership>(
              `/api/tools/${Tool.BlackDuck}/project_groups/${projectGroupId}/user/${userId}`
            )
            .then((res) => {
              if (JSON.stringify(res.data) !== JSON.stringify(task))
                setMembership(res.data);
            });
        }, DURATION);
      }
    }

    return () => clearInterval(intervalId);
  }, [client, membership?.task, projectGroupId, userId]);

  return { membership, loading, error, reload: loadMembership };
}

export function useBlackDuckProjectGroupManager(projectGroupId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [manager, setManager] = useState<User | null>(null);

  const client = useContext(HttpClientContext);

  const loadMembership = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.get<User>(
        `/api/tools/${Tool.BlackDuck}/project_groups/${projectGroupId}/manager`
      )
      .then((res) => setManager(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [client, projectGroupId]);

  useEffect(() => {
    loadMembership();
  }, [loadMembership]);

  return {
    manager,
    loading,
    error,
    reload: loadMembership,
  };
}

export function useDatasetPendingTask(
  collectionId: string,
  datasetId: string,
  userId: number
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [task, setTask] = useState<Task | null>(null);

  const client = useContext(HttpClientContext);

  const loadTask = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.get<Task>(`/api/datasets/${collectionId}/${datasetId}/${userId}/task`)
      .then((res) => setTask(res.data))
      .catch((e: AxiosError) => {
        if (e.response?.status === 404) setTask(null);
        else setError(e.response?.headers[X_REQUEST_ID]);
      })
      .finally(() => setLoading(false));
  }, [client, collectionId, datasetId, userId]);

  useEffect(() => {
    loadTask();
  }, [loadTask]);

  useEffect(() => {
    let intervalId: string | number | NodeJS.Timeout | undefined;

    if (task !== null) {
      if (task.status.kind !== "Done" && task.status.kind !== "Cancelled") {
        intervalId = setInterval(() => {
          client?.get<Task>(`/api/tasks/${task.id}`).then((res) => {
            if (JSON.stringify(res.data) !== JSON.stringify(task))
              setTask(res.data);
          });
        }, DURATION);
      } else {
        setTask(null);
      }
    }

    return () => clearInterval(intervalId);
  }, [client, task]);

  return { task, loading, error, reload: loadTask };
}
